<template>
  <apexchart
    type="rangeBar"
    height="200"
    :options="chartOptions"
    :series="plotSeries"
    class="contests-plot"
  />
</template>

<script>
import { isPlayerGroupQA             } from '../model/event.js';
import { getPlayerGroupName          } from '../model/event.js';
import { getPlayerGroupPublicity     } from '../model/event.js';
import { getPlayerGroupPublicityName } from '../model/event.js';

/**
 * @typedef { import('../model/contest.js').ContestPreview } ContestPreview
 */
import { isContestActive                 } from '../model/contest.js';
import { isContestFinished               } from '../model/contest.js';
import { isContestClosed                 } from '../model/contest.js';
import { getContestTypeName              } from '../model/contest.js';
import { getContestTypeNameFull          } from '../model/contest.js';
import { getSortedContestTypes           } from '../model/contest.js';
import { getContestPlannedDurationStr    } from '../model/contest.js';
import { getContestRealDurationStrSimple } from '../model/contest.js';

import { getMomentMinStr                    } from '../util.js';
import { getMomentMinStrOptionalWithDefault } from '../util.js';
import { convertMomentToSec                 } from '../util.js';

import VueApexCharts from 'vue-apexcharts';

export default {
  name: 'ContestsPlot',

  components: {
    apexchart: VueApexCharts
  },

  props: {
    contests: {
      type: Array,
      required: true,
    },

    windowPeriod: {
      type: Object,
      default: null,
    }
  },

  data() {
    return {
      // Fix width error.
      // https://github.com/apexcharts/apexcharts.js/issues/1077
      canShowData: false,

      selectedContestId: null,
    };
  },

  computed: {
    /** @returns {Object} */
    chartOptions() {
      if(!this.canShowData) {
        return {};
      }

      const now = new Date();
      const nowMsec = now.getTime();

      const xRange = getXRange(now, this.contests, this.windowPeriod);

      return {
        chart: {
          height: 220,
          type: 'rangeBar',

          toolbar: {
            offsetY: -12,
          },

          animations: {
            enabled: false
          },
          events: {
            dataPointSelection: this.handleDataPointSelection,
            click: this.handleDataPointMouseEnter,
            beforeResetZoom: this.handleBeforeResetZoom,
          },
        },
        annotations: {
          xaxis: [
            {
              x: nowMsec,
              borderColor: '#00C853', // #775dd0, #00e396
              label: {
                style: {
                  color: '#00C853',
                },
                text: 'now',
                orientation: 'horizontal',
                offsetY: -8,
              }
            }
          ]
        },
        plotOptions: {
          bar: {
            horizontal: true
          }
        },
        xaxis: {
          type: 'datetime',

          min: xRange.min,
          max: xRange.max,

          labels: {
            datetimeUTC: false,
            datetimeFormatter: {
              day: 'MMM d',
            },
          },
        },
        grid: {
          padding: {
            left: 0
          }
        },
        tooltip: {
          custom: createTooltip,
        },
      };
    },

    /** @returns {Object[]} */
    plotSeries() {
      if(!this.canShowData) {
        return [];
      }

      const data = [];

      for(const contest of this.contests) {
        const contestData = {
          x: getLineName(contest),
          y: [
            getContestBegin(contest) * 1000,
            getContestEnd(contest)   * 1000
          ],
          contest: contest,
        };

        if(isContestActive(contest.status)) {
          contestData.fillColor = '#00E396'; // green
        }
        if(isContestFinished(contest.status)) {
          contestData.fillColor = '#fcbd46';
        }
        if(isContestClosed(contest.status)) {
          contestData.fillColor = '#bdbdbd';
        }

        data.push(contestData);
      }

      data.sort(comparePlotItems);

      const series = [
        {data: data},
      ];
      return series;
    },
  },

  created() {
    this.$nextTick(() => {
      this.canShowData = true;
    });
  },

  methods: {
    handleDataPointSelection(event, chartContext, config) {
      const seriesIndex = config.seriesIndex;
      const dataPointIndex = config.dataPointIndex;

      const series = config.w.config.series;

      const dataPoint = series[seriesIndex].data[dataPointIndex];
      const contest = dataPoint.contest;

      this.selectedContestId = contest.id;
    },

    handleDataPointMouseEnter() {
      const selectedContestId = this.selectedContestId;
      if(selectedContestId !== null) {
         this.$emit('contest-clicked', selectedContestId);
      }
    },

    handleBeforeResetZoom() {
      const now = new Date();
      const xRange = getXRange(now, this.contests, this.windowPeriod);
      return {xaxis: xRange};
    },
  },
  
};

//-----------------------------------------------------------------------------

/**
 * @param {ContestPreview} contest
 * @returns {number}
 */
function getContestBegin(contest) {
  const srtime = contest.srtime;
  if(srtime > 0) {
    return srtime;
  }

  return contest.stime;
}

/**
 * @param {ContestPreview} contest
 * @returns {number}
 */
function getContestEnd(contest) {
  const frtime = contest.frtime;
  if(frtime > 0) {
    return frtime;
  }

  return contest.ftime;
}

//-----------------------------------------------------------------------------

function comparePlotItems(dataA, dataB) {
  const contestA = dataA.contest;
  const contestB = dataB.contest;

  if(contestA.type !== contestB.type) {
    for(const contestType of getSortedContestTypes()) {
      if(contestA.type === contestType) {
        return -1;
      }
      if(contestB.type === contestType) {
        return 1;
      }
    }
  }

  const groupA = contestA.playerGroupId;
  const groupB = contestB.playerGroupId;
  if(groupA !== groupB) {
    return isPlayerGroupQA(contestA.playerGroupId) ? -1 : 1;
  }

  return 0;
}

/**
 * @typedef {Object} MsecRange
 * @property {number} min
 * @property {number} max
 */

/**
 * @param {Date} nowDate
 * @param {ContestPreview[]} contests
 * @returns {MsecRange}
 */
function getXRange(nowDate, contests, window) {
  const now = convertMomentToSec(nowDate);

  const contestsBegin = getMinBeginSec(contests);
  const begin = calcBegin(now, contestsBegin, window.begin);

  const contestsEnd = getMaxEndSec(contests);
  const end = calcEnd(now, contestsEnd, window.end);

  const len = end - begin;

  const PADDING_PERCENT = 1;
  const padding = (len / 100) * PADDING_PERCENT;

  return {
    min: (begin - padding) * 1000,
    max: (end   + padding) * 1000,
  };
}

/**
 * @param {number} now
 * @param {number} contestsBegin
 * @param {number} windowBegin
 * @returns {number}
 */
function calcBegin(now, contestsBegin, windowBegin) {
  // Параметр windowBegin может быть равен 0
  // (нужно показать все события в прошлом).
  if(windowBegin === 0) {
    return Math.min(now, contestsBegin);
  }

  // Если параметр windowBegin указан, то параметр now не нужно учитывать
  // (т.к. значение windowBegin расположено всегда левее, чем now).
  return Math.max(contestsBegin, windowBegin);
}

/**
 * @param {number} now
 * @param {number} contestsEnd
 * @param {number} windowEnd
 * @returns {number}
 */
function calcEnd(now, contestsEnd, windowEnd) {
  // Параметр windowEnd может быть равен 0
  // (нужно показать все события в будущем).
  if(windowEnd === 0) {
    return Math.max(now, contestsEnd);
  }

  // Если параметр windowEnd указан, то параметр now не нужно учитывать
  // (т.к. значение windowEnd расположено всегда правее, чем now).
  return Math.min(contestsEnd, windowEnd);
}

/**
 * @param {ContestPreview[]} contests
 * @returns {number}
 */
function getMinBeginSec(contests) {
  let minBegin = getContestBegin(contests[0]);

  for(const contest of contests) {
    const begin = getContestBegin(contest);
    if(minBegin > begin) {
      minBegin = begin;
    }
  }

  return minBegin;
}

/**
 * @param {ContestPreview[]} contests
 * @returns {number}
 */
function getMaxEndSec(contests) {
  let maxEnd = getContestEnd(contests[0]);

  for(const contest of contests) {
    const end = getContestEnd(contest);
    if(maxEnd < end) {
      maxEnd = end;
    }
  }

  return maxEnd;
}

function createTooltip(param) {
  const seriesIndex = param.seriesIndex;
  const dataPointIndex = param.dataPointIndex;

  const sObject = param.w.config.series[seriesIndex];
  const dataObject = sObject.data[dataPointIndex];
  const contest = dataObject.contest;
  const contestIdStr = contest.id.toString();

  const typeName = getContestTypeNameFull(contest.type)
  const playerGroupName = getPlayerGroupName(contest.playerGroupId);

  const stimeStr = getMomentMinStr(contest.stime);
  const ftimeStr = getMomentMinStr(contest.ftime);
  const plannedDurationStr = getContestPlannedDurationStr(contest)

  const srtimeStr = getMomentMinStrOptionalWithDefault(contest.srtime, '---');
  const frtimeStr = getMomentMinStrOptionalWithDefault(contest.frtime, '---');
  const realDurationStr = getContestRealDurationStrSimple(contest)

  let code = '<div class="contests-plot-tooltip">';
  code += `<span class="header">Contest ${contestIdStr}</span>`;

  code += '<p class="mb-1">';
  code +=   `Type: ${typeName}<br>`
  code +=   `Segment: ${playerGroupName}`
  code += '</p>';

  code += '<table>';
  code +=   '<tr>';
  code +=     '<td></td>';
  code +=     '<th class="h">begin</th>';
  code +=     '<th class="h">end</th>';
  code +=     '<th class="h">duration</th>';
  code +=   '</tr>';
  code +=   '<tr>';
  code +=     '<th>plan</th>';
  code +=     `<td>${stimeStr}</td>`;
  code +=     `<td>${ftimeStr}</td>`;
  code +=     `<td>${plannedDurationStr}</td>`;
  code +=   '</tr>';

  if(contest.srtime > 0) {
    code +=   '<tr>';
    code +=     '<th>real</th>';
    code +=     `<td>${srtimeStr}</td>`;
    code +=     `<td>${frtimeStr}</td>`;
    code +=     `<td>${realDurationStr}</td>`;
    code +=   '</tr>';
  }

  code += '</table>';
  code += '</div>';

  return code;
}

function getLineName(contest) {
  const publicity = getPlayerGroupPublicity(contest.playerGroupId);
  const publicityName = getPlayerGroupPublicityName(publicity);
        
  return getContestTypeName(contest.type) + ' (' + publicityName + ')';
}
</script>

<style>
.contests-plot .apexcharts-tooltip.apexcharts-theme-light {
  background: rgba(255, 255, 255, 0.8);
}

.contests-plot-tooltip {
  padding: 0.3rem;
}

.contests-plot-tooltip .header {
  font-weight: bold;
}

.contests-plot-tooltip th.h,
.contests-plot-tooltip td {
  padding-left: .5rem;
}

/**
  При наведении на прямоугольник (который обозначает проведение конкурса)
  курсор должен принимать форму руки с указательным пальцем.
*/
.contests-plot .apexcharts-rangebar-area {
  cursor: pointer;
}
</style>
